import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicIntakeForm = () => {
  const { slug } = useParams();
  const [formConfig, setFormConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  // Form fields
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    const fetchFormConfig = async () => {
      try {
        const response = await fetch(`${API}/public/intake/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setFormConfig(data);
        } else if (response.status === 404) {
          setError('This form is no longer available or the link is incorrect.');
        } else {
          setError('Unable to load the form. Please try again later.');
        }
      } catch (err) {
        console.error('Error fetching form:', err);
        setError('Unable to load the form. Please check your connection.');
      } finally {
        setLoading(false);
      }
    };

    fetchFormConfig();
  }, [slug]);

  const validateForm = () => {
    const errors = {};
    
    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!title) {
      errors.title = 'Please select a title';
    }
    
    if (!description.trim()) {
      errors.description = 'Description is required';
    } else if (description.trim().length < 10) {
      errors.description = 'Please provide more details (at least 10 characters)';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      const response = await fetch(`${API}/public/intake/${slug}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          submitter_email: email.trim(),
          title,
          description: description.trim()
        })
      });
      
      if (response.ok) {
        setSubmitted(true);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to submit. Please try again.');
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError('Failed to submit. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setTitle('');
    setDescription('');
    setFieldErrors({});
    setSubmitted(false);
    setError(null);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-zinc-900 to-zinc-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-zinc-900/80 border-zinc-800">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
            <p className="text-zinc-400">Loading form...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state (form not found)
  if (error && !formConfig) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-zinc-900 to-zinc-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-zinc-900/80 border-zinc-800">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Form Not Available</h2>
            <p className="text-zinc-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-zinc-900 to-zinc-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-zinc-900/80 border-zinc-800">
          {/* Header */}
          <CardHeader className="border-b border-zinc-800 bg-zinc-950/50">
            <div className="flex items-center justify-center">
              <img 
                src="/logo-dark.png" 
                alt="2BeLive TaskFlow AI" 
                className="h-10"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          </CardHeader>
          
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-semibold text-white mb-3">Thank you for submitting!</h2>
            <p className="text-zinc-400 mb-6">We will get in touch with you soon.</p>
            <Button
              variant="outline"
              onClick={resetForm}
              className="border-zinc-700 hover:bg-zinc-800"
              data-testid="submit-another-btn"
            >
              Submit Another Request
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Form state
  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-900 to-zinc-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg bg-zinc-900/80 border-zinc-800">
        {/* Header */}
        <CardHeader className="border-b border-zinc-800 bg-zinc-950/50">
          <div className="flex flex-col items-center gap-3">
            <img 
              src="/logo-dark.png" 
              alt="2BeLive TaskFlow AI" 
              className="h-10"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <CardTitle className="text-xl text-center text-white">
              {formConfig?.name || 'Submit a Request'}
            </CardTitle>
          </div>
        </CardHeader>
        
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">
                Email Address *
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (fieldErrors.email) {
                    setFieldErrors({ ...fieldErrors, email: null });
                  }
                }}
                placeholder="your@email.com"
                className={`bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 ${
                  fieldErrors.email ? 'border-red-500' : ''
                }`}
                data-testid="intake-email-input"
              />
              {fieldErrors.email && (
                <p className="text-sm text-red-400">{fieldErrors.email}</p>
              )}
            </div>
            
            {/* Title Selection */}
            <div className="space-y-2">
              <Label htmlFor="title" className="text-zinc-300">
                Title *
              </Label>
              <Select 
                value={title} 
                onValueChange={(val) => {
                  setTitle(val);
                  if (fieldErrors.title) {
                    setFieldErrors({ ...fieldErrors, title: null });
                  }
                }}
              >
                <SelectTrigger 
                  className={`bg-zinc-800/50 border-zinc-700 text-white ${
                    fieldErrors.title ? 'border-red-500' : ''
                  } ${!title ? 'text-zinc-500' : ''}`}
                  data-testid="intake-title-select"
                >
                  <SelectValue placeholder="Select a title" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {formConfig?.titles?.map((t, idx) => (
                    <SelectItem 
                      key={idx} 
                      value={t}
                      className="text-white hover:bg-zinc-700 focus:bg-zinc-700"
                    >
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldErrors.title && (
                <p className="text-sm text-red-400">{fieldErrors.title}</p>
              )}
            </div>
            
            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description" className="text-zinc-300">
                Description *
              </Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  if (fieldErrors.description) {
                    setFieldErrors({ ...fieldErrors, description: null });
                  }
                }}
                placeholder="Please describe your request in detail..."
                rows={5}
                className={`bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 resize-none ${
                  fieldErrors.description ? 'border-red-500' : ''
                }`}
                data-testid="intake-description-input"
              />
              {fieldErrors.description && (
                <p className="text-sm text-red-400">{fieldErrors.description}</p>
              )}
            </div>
            
            {/* Error message */}
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}
            
            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full"
              disabled={submitting}
              data-testid="intake-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          </form>
        </CardContent>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-800 bg-zinc-950/30">
          <p className="text-xs text-center text-zinc-500">
            Powered by 2BeLive TaskFlow AI
          </p>
        </div>
      </Card>
    </div>
  );
};

export default PublicIntakeForm;
